import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../lib/api";

/** ---------------- Types aligned to backend ---------------- */
type CustomerType = "individual" | "business" | string;

type UserRow = {
  id: number;
  username: string;
  email: string;
  customer_type: CustomerType;
  role?: string | null;
  admin_role?: string | null;
  email_verified_at?: string | null;
  phone_verified_at?: string | null;
  phone?: string | null;
  require_pof?: 0 | 1;
  require_sow?: 0 | 1;
  pof_status?: "pending" | "approved" | "rejected" | null;
  sow_status?: "pending" | "approved" | "rejected" | null;
  risk_score?: number | null;
  risk_level?: "low" | "medium" | "high" | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type BusinessProfile = {
  company_name?: string | null;
  company_number?: string | null;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
  company_phone?: string | null;
};

type UserData = {
  first_name?: string | null;
  last_name?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  phone?: string | null;
};

type UboRow = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  nationality?: string | null;
  birthdate?: string | null;
  residential_address?: string | null;
  ownership_percent?: number | string | null;
  status?: "pending" | "approved" | "rejected" | null;
  require_id_reupload?: boolean;
  require_poa_reupload?: boolean;
  review_notes?: string | null;
};

type KycStatus = {
  status?: "pending" | "approved" | "rejected" | "not_started";
  review_notes?: string | null;
  /** tolerate alternative fields from older APIs */
  admin_notes?: string | null;
  notes?: string | null;
};

type KybStatus = {
  status?: "pending" | "approved" | "rejected" | "not_started";
  review_notes?: string | null;
  /** tolerate alternative fields from older APIs */
  admin_notes?: string | null;
  notes?: string | null;
};

/** ---------------- Helpers ---------------- */
function joinAddress(parts: Array<string | null | undefined>) {
  return (parts.filter(Boolean) as string[]).join(", ") || "—";
}

function Badge({ tone, children }: { tone: "green" | "yellow" | "red" | "gray"; children: React.ReactNode }) {
  const map = {
    green: "text-green-800 bg-green-100",
    yellow: "text-yellow-800 bg-yellow-100",
    red: "text-red-800 bg-red-100",
    gray: "text-gray-700 bg-gray-100",
  } as const;
  return <span className={`inline-block px-2 py-1 rounded text-xs ${map[tone]}`}>{children}</span>;
}

function StatusBadge({ s }: { s?: string | null }) {
  if (s === "approved") return <Badge tone="green">Approved</Badge>;
  if (s === "pending") return <Badge tone="yellow">Pending</Badge>;
  if (s === "rejected") return <Badge tone="red">Rejected</Badge>;
  return <Badge tone="gray">Not submitted</Badge>;
}

/** Keep date-only, no time */
function onlyDate(d?: string | null) {
  if (!d) return "—";
  const s = String(d);
  if (s.includes("T")) return s.split("T")[0]; // ISO string
  if (s.length > 10) return s.slice(0, 10); // fallback
  return s; // already yyyy-mm-dd
}

/** ---------------- Page ---------------- */
export default function ProfileOverview() {
  const [user, setUser] = useState<UserRow | null>(null);
  const [biz, setBiz] = useState<BusinessProfile | null>(null);
  const [ud, setUd] = useState<UserData | null>(null);
  const [ubos, setUbos] = useState<UboRow[]>([]);
  const [kyc, setKyc] = useState<KycStatus | null>(null);
  const [kyb, setKyb] = useState<KybStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const isBiz = useMemo(() => user?.customer_type === "business", [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const me = await apiGet<any>("/xapi/me");
        const u: UserRow | undefined = me?.user ?? me?.data?.user ?? me;
        if (!cancelled && u) setUser(u);

        if (u?.customer_type === "business") {
          // --- Business path ---
          try {
            const b = await apiGet<BusinessProfile>("/xapi/profile/business");
            if (!cancelled) setBiz(b || null);
          } catch {}

          try {
            const r = await apiGet<{ data?: UboRow[]; ubos?: UboRow[]; items?: UboRow[] }>("/xapi/ubos");
            const list = r?.data ?? r?.ubos ?? r?.items ?? [];
            if (!cancelled) setUbos(list);
          } catch {}

          try {
            const s = await apiGet<KybStatus>("/xapi/kyb/status");
            if (!cancelled) setKyb(s ?? null);
          } catch {}
        } else {
          // --- Personal path ---
          try {
            const p = await apiGet<UserData>("/xapi/profile/personal");
            if (!cancelled) setUd(p || null);
          } catch {}

          try {
            const s = await apiGet<KycStatus>("/xapi/kyc/status");
            if (!cancelled) setKyc(s ?? null);
          } catch {}
        }
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || "Failed to load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /** tolerant fallback for notes fields */
  const kycNotes = kyc?.review_notes ?? kyc?.admin_notes ?? kyc?.notes ?? null;
  const kybNotes = kyb?.review_notes ?? kyb?.admin_notes ?? kyb?.notes ?? null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* ---- Header ---- */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-800">Profile Overview</h1>
      </div>

      {/* ---- Tabs ---- */}
      <nav className="flex space-x-4 border-b mb-6">
        <Link to="/profile" className="text-sm border-b-2 border-blue-600 text-blue-600 px-3 py-2">
          Overview
        </Link>
        <Link to="/profile/edit" className="text-sm text-gray-600 hover:text-blue-600 px-3 py-2">
          Edit Profile
        </Link>

        {user?.customer_type === "individual" && (
          <Link to="/kyc" className="text-sm text-gray-600 hover:text-blue-600 px-3 py-2">
            KYC Status
          </Link>
        )}
        {user?.customer_type === "business" && (
          <Link to="/kyb/status" className="text-sm text-gray-600 hover:text-blue-600 px-3 py-2">
            KYB Status
          </Link>
        )}

        <Link to="/profile/security" className="text-sm text-gray-600 hover:text-blue-600 px-3 py-2">
          Security
        </Link>
        {user?.customer_type === "business" && (
          <Link to="/profile/users" className="text-sm text-gray-600 hover:text-blue-600 px-3 py-2">
            Manage Users
          </Link>
        )}
      </nav>

      {/* ---- Loading / Error ---- */}
      {loading && <div className="card-box p-6">Loading…</div>}
      {!!err && !loading && <div className="alert-danger mb-6">{err}</div>}

      {/* ---- Profile Details ---- */}
      {!loading && user && (
        <div className="bg-white rounded p-6 shadow mt-8">
          <h3 className="text-lg font-bold mb-4">User Information</h3>
          <dl className="text-sm grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-gray-700">
            <div>
              <dt className="font-semibold">Username:</dt>
              <dd>{user.username}</dd>
            </div>
            <div>
              <dt className="font-semibold">Email:</dt>
              <dd>{user.email}</dd>
            </div>
            <div>
              <dt className="font-semibold">Type:</dt>
              <dd>{(user.customer_type ?? "").charAt(0).toUpperCase() + (user.customer_type ?? "").slice(1)}</dd>
            </div>

            {isBiz ? (
              <>
                <div>
                  <dt className="font-semibold">Company Name:</dt>
                  <dd>{biz?.company_name ?? "—"}</dd>
                </div>
                <div>
                  <dt className="font-semibold">Company Registration Number:</dt>
                  <dd>{biz?.company_number ?? "—"}</dd>
                </div>
                <div>
                  <dt className="font-semibold">Company Address:</dt>
                  <dd>{joinAddress([biz?.address, biz?.postal_code, biz?.city, biz?.country])}</dd>
                </div>
                <div>
                  <dt className="font-semibold">Phone:</dt>
                  <dd>{biz?.phone ?? biz?.company_phone ?? "—"}</dd>
                </div>
              </>
            ) : (
              <>
                <div>
                  <dt className="font-semibold">Full Name:</dt>
                  <dd>
                    {ud?.first_name || ud?.last_name
                      ? `${ud?.first_name ?? ""} ${ud?.last_name ?? ""}`.trim()
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold">Address:</dt>
                  <dd>
                    {ud
                      ? joinAddress([
                          ud.address_line1,
                          ud.address_line2,
                          ud.postal_code,
                          ud.city,
                          ud.country,
                        ])
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold">Mobile Phone:</dt>
                  <dd>{ud?.phone ?? "—"}</dd>
                </div>
              </>
            )}
          </dl>

          <div className="mt-6">
            <Link
              to="/profile/edit"
              className="text-sm px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-100 hover:text-gray-900 transition"
            >
              Edit Profile
            </Link>
          </div>
        </div>
      )}

      {/* ---- UBOs (business only) ---- */}
      {!loading && isBiz && (
        <div className="bg-white rounded p-6 shadow mt-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold">Ultimate Beneficial Owners (UBOs)</h3>
            <Link
              to="/profile/ubos/create"
              className="text-sm px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-100 hover:text-gray-900 transition"
            >
              + Add UBO
            </Link>
          </div>
          {ubos && ubos.length > 0 ? (
            <div className="space-y-4">
              {ubos.map((ubo) => {
                const name = `${ubo.first_name ?? ""} ${ubo.last_name ?? ""}`.trim() || "—";
                return (
                  <div key={ubo.id} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                      <div>
                        <p className="font-semibold text-gray-800">{name}</p>
                        <p className="text-sm text-gray-600">
                          Nationality: {ubo.nationality ?? "—"}
                          <br />
                          Birthdate: {onlyDate(ubo.birthdate)}
                          <br />
                          Address: {ubo.residential_address ?? "—"}
                          <br />
                          Ownership:{" "}
                          {ubo.ownership_percent != null
                            ? `${Number(ubo.ownership_percent).toFixed(2)}%`
                            : "—"}
                        </p>
                      </div>
                      <div className="mt-2 md:mt-0">
                        <StatusBadge s={ubo.status} />
                      </div>
                    </div>

                    {ubo.status !== "approved" &&
                      (ubo.require_id_reupload || ubo.require_poa_reupload || ubo.review_notes) && (
                        <div className="mt-4 bg-yellow-50 p-3 rounded text-sm text-gray-800">
                          <p className="font-semibold mb-1">UBO Action Required</p>
                          {ubo.require_id_reupload && (
                            <p>
                              ⬆️ The UBO must redo their <strong>ID document</strong> in their own KYC flow.
                            </p>
                          )}
                          {ubo.require_poa_reupload && (
                            <p>
                              ⬆️ The UBO must redo their <strong>Proof of Address</strong> in their own KYC flow.
                            </p>
                          )}
                          {ubo.review_notes && (
                            <p className="italic text-gray-700 mt-1">📝 {ubo.review_notes}</p>
                          )}

                          <div className="mt-3 flex flex-wrap gap-2">
                            <Link
                              to={`/profile/ubos/${ubo.id}/edit`}
                              className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded text-xs"
                            >
                              ✏️ Edit UBO / Manage KYC
                            </Link>
                            {/* Upload flow removed: no more /upload link */}
                          </div>

                          <p className="mt-2 text-xs text-gray-600">
                            Documents are no longer uploaded from this business profile. Instead, send or
                            re-send the UBO their KYC invite from the edit page so they can upload ID,
                            selfie and proof of address themselves.
                          </p>
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-600">No UBOs added yet.</p>
          )}
        </div>
      )}

      {/* ---- KYC/KYB Status partial ---- */}
      {!loading && user && (
        <div className="bg-white rounded p-6 shadow mt-8">
          <h3 className="text-lg font-bold mb-4">{isBiz ? "KYB Status" : "KYC Status"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="font-semibold mb-1">Status</div>
              <div>
                <StatusBadge s={(isBiz ? kyb?.status : kyc?.status) ?? "not_started"} />
              </div>
            </div>
            <div>
              <div className="font-semibold mb-1">Review Notes</div>
              <div className="text-gray-700 italic">
                {(isBiz ? kybNotes : kycNotes) ?? "—"}
              </div>
            </div>
          </div>

          <div className="mt-6">
            {isBiz ? (
              <Link to="/kyb/status" className="btn btn-white">
                Open KYB
              </Link>
            ) : (
              <Link to="/kyc" className="btn btn-white">
                Open KYC
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
